/**
 * Single-source-of-truth for the bearer token + refresh token. The
 * axios interceptor reads the access token from here on every request
 * so updates take effect immediately (no module-load-time capture —
 * that was the bug we fixed in the v1 frontend's api/modules/* files).
 *
 * sessionStorage rather than localStorage so tab close = logout — matches
 * v1 behaviour and reduces XSS-token-theft blast radius. We may move
 * this to a HttpOnly cookie in a later PR; the surface here doesn't
 * change.
 *
 * Refresh token: stored alongside the access token so a tab refresh
 * within session lifetime doesn't force re-login. AUTH-2's
 * `/auth/refresh` endpoint takes the refresh token and returns a new
 * pair; the wiring there can read this store.
 */

const KEY = 'absc.v2.token';
const KEY_REFRESH = 'absc.v2.refresh';

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export const tokenStore = {
  get(): string | null {
    try {
      return sessionStorage.getItem(KEY);
    } catch {
      return null;
    }
  },

  set(token: string): void {
    try {
      sessionStorage.setItem(KEY, token);
    } catch {
      /* sessionStorage may be unavailable in some embeds — degrade gracefully */
    }
    listeners.forEach((l) => l(token));
  },

  clear(): void {
    try {
      sessionStorage.removeItem(KEY);
      sessionStorage.removeItem(KEY_REFRESH);
    } catch {
      /* see above */
    }
    listeners.forEach((l) => l(null));
  },

  /** Refresh-token accessors. Kept as separate methods so the access
   *  token's listener API doesn't accidentally fire on refresh updates. */
  getRefresh(): string | null {
    try { return sessionStorage.getItem(KEY_REFRESH); } catch { return null; }
  },
  setRefresh(token: string): void {
    try { sessionStorage.setItem(KEY_REFRESH, token); } catch { /* see above */ }
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
